import type { IntakeAnswers, IntakeQuestion } from '@/api/portalIntake';
import { isAnswered } from '@/lib/portalIntake';

/** Index of the question the wizard should resume at: the first
 *  unanswered one, or the last question when everything is answered
 *  (so a returning customer lands on the finish step, not question 1). */
export function resumeIndex(questions: IntakeQuestion[], answers: IntakeAnswers): number {
  const first = questions.findIndex((q) => !isAnswered(answers[q.id]));
  if (first === -1) return Math.max(questions.length - 1, 0);
  return first;
}

/** Required questions demand an answer before Next; optional ones can
 *  always advance (Skip is the same transition without an answer). */
export function canAdvance(question: IntakeQuestion, answers: IntakeAnswers): boolean {
  return !question.required || isAnswered(answers[question.id]);
}

export interface WizardProgress {
  /** 1-based position for the "Question N of M" label. */
  position: number;
  total: number;
  /** Completed-steps count for the progress bar (current step exclusive). */
  done: number;
}

export function wizardProgress(index: number, total: number): WizardProgress {
  const clamped = Math.min(Math.max(index, 0), Math.max(total - 1, 0));
  return { position: clamped + 1, total, done: clamped };
}

/** True when every required question is answered — the submit gate,
 *  mirrored client-side so the finish button state matches the server. */
export function requiredComplete(questions: IntakeQuestion[], answers: IntakeAnswers): boolean {
  return questions.every((q) => !q.required || isAnswered(answers[q.id]));
}
